import type React from 'react';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';
import { Outlet, useNavigation } from 'react-router';

import { LrmWarningDialogManager } from '@/components/lrmWarning/LrmWarningDialogManager.comp';
import { VersionChecker } from '@/components/versionChecker/VersionChecker.comp';
import { CopyObjectContextProvider } from '@/contexts/copyObject/CopyObjectContextProvider.util';
import { DictionariesContextProvider } from '@/contexts/dictionaries/DictionariesContextProvider.util';
import { NotificationsContextProvider } from '@/contexts/notifications/NotificationsContextProvider.util';
import { UserContextProvider } from '@/contexts/userContext/UserContextProvider.util.tsx';
import { UserAwareErrorBoundary } from '@/errorHandling/ErrorBoundary.comp.tsx';
import { LastVisitedLocationController } from '@/layouts/controllers/LastVisitedLocation.controller.tsx';
import { LogoutController } from '@/layouts/controllers/Logout.controller.tsx';
import { ScrollRestorationController } from '@/layouts/controllers/ScrollRestoration.controller.tsx';
import { UserInitializeController } from '@/layouts/controllers/UserInitialize.controller';
import { PageLoading } from '@/routes/PageLoading.comp.tsx';

const LOADING_SPINNER_DELAY_MS = 300;

/**
 * Turns true only when `active` has lasted `delayMs`, so brief loading
 * phases never surface to the user.
 */
const useDelayedFlag = (active: boolean, delayMs: number): boolean => {
  const [timedOut, setTimedOut] = useState(false);

  if (!active && timedOut) {
    setTimedOut(false);
  }

  useEffect(() => {
    if (!active) {
      return;
    }
    const timer = window.setTimeout(() => setTimedOut(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [active, delayMs]);

  return active && timedOut;
};

/**
 * During a navigation the previous view stays mounted while the next one
 * prepares, so the view transition runs old → new directly. Swapping to
 * PageLoading at navigation start (OCD-308) tore the content down and
 * flashed a spinner on every view switch once route loaders landed
 * (OCD-309); the spinner is now a fallback for loads that actually take
 * a while (first lazy chunk, slow network).
 */
export const RouteContent: React.FC = () => {
  const isLoading = useNavigation().state === 'loading';
  const slowLoad = useDelayedFlag(isLoading, LOADING_SPINNER_DELAY_MS);

  return slowLoad ? <PageLoading /> : <Outlet />;
};

/**
 * Pathless root layout route element: providers and cross-cutting controllers
 * only. Screen chrome lives in AppLayout / BareLayout groups (OCD-308); this
 * layout renders the matched group via <Outlet />.
 */
export const RootLayout: React.FC = () => (
  <>
    <LogoutController />
    <LastVisitedLocationController />
    <ScrollRestorationController />
    <UserContextProvider>
      <UserAwareErrorBoundary>
        <DictionariesContextProvider>
          <NotificationsContextProvider>
            <CopyObjectContextProvider>
              <UserInitializeController />
              <ReactQueryDevtools initialIsOpen={false} />
              <LrmWarningDialogManager />
              {/* Shared across app and bare groups so version prompts keep covering every screen */}
              <VersionChecker />
              <Outlet />
            </CopyObjectContextProvider>
          </NotificationsContextProvider>
        </DictionariesContextProvider>
      </UserAwareErrorBoundary>
    </UserContextProvider>
  </>
);
